import { useContext, useCallback, createContext } from 'react';
import { pushEvent } from '../../client/data-layer';
import type {
  FilterScope,
  FilterLayouts,
  FilterTypes,
} from 'src/globals/tracking/filter';
import { trackFilterSelectEvent } from 'src/globals/tracking/filter';
import throttle from 'lodash/throttle';

export const ProductCollectionFilterContext = createContext({
  filterLayout: null as null | FilterLayouts,
});

export const ProductCollectionTrackingContext = createContext({
  categoryId: null as string | null | undefined,
  categoryName: null as string | null | undefined,
});

export const ProductCollectionFilterTrackingScopeContext = createContext({
  scope: null as null | FilterScope,
});

type FilterEventProps = {
  type: FilterTypes;
  property: string;
  title?: string;
};

export const useTrackFilterSelect = ({
  type,
  property,
  title,
}: FilterEventProps) => {
  const { categoryId, categoryName } = useContext(
    ProductCollectionTrackingContext
  );
  const { filterLayout } = useContext(ProductCollectionFilterContext);
  const { scope } = useContext(ProductCollectionFilterTrackingScopeContext);

  const trackFilterSelectForProductCollection = useCallback(
    (data: { value: string }) => {
      // Push old Universal Analytics event
      pushEvent('productCollectionFilterUpdate', {
        type,
        title,
        value: data.value,
        property,
        categoryId,
        categoryName,
        filterLayout,
      });
      // Track new event
      trackFilterSelectEvent({
        value: data.value,
        scope,
        property,
        category: categoryId,
        layout: filterLayout,
        type,
      });
    },
    [scope, property, categoryId, filterLayout, type, title, categoryName]
  );

  const trackFilterSelectWithThrottle = useCallback(
    // Only send an event evert 4s.
    throttle(trackFilterSelectForProductCollection, 4000),
    [trackFilterSelectForProductCollection]
  );

  return {
    trackFilterSelect: trackFilterSelectForProductCollection,
    trackFilterSelectWithThrottle,
  };
};

export const useTrackClearFilter = () => {
  const { categoryId, categoryName } = useContext(
    ProductCollectionTrackingContext
  );
  const clearFilter = useCallback(
    (property: string) => {
      pushEvent('productCollectionFilterClear', {
        property,
        categoryId,
        categoryName,
      });
    },
    [categoryId, categoryName]
  );
  return clearFilter;
};
